/**
 * 将扁平商品列表组装为 产品(product_id) → 主规格(颜色变体) → SKU 三级树。
 * TikTok 无 SPU/SKC；主规格按 SKU sales_attributes 中的颜色拆分。
 */

function textOf(...vals) {
  for (const v of vals) {
    const s = v == null ? '' : String(v).trim();
    if (s) return s;
  }
  return '';
}

function salesAttrsOf(sku) {
  if (Array.isArray(sku?.sales_attributes)) return sku.sales_attributes;
  if (Array.isArray(sku?.salesAttributes)) return sku.salesAttributes;
  return [];
}

function pickColorFromSku(sku) {
  const attrs = salesAttrsOf(sku);
  if (!attrs.length) {
    return { key: 'default', label: '', image: '', attr: null };
  }
  const colorAttr = attrs.find((a) => /color|colour|颜色|色彩/i.test(String(a?.name || '')))
    || attrs[0];
  const valueId = textOf(colorAttr?.value_id, colorAttr?.valueId);
  const valueName = textOf(colorAttr?.value_name, colorAttr?.valueName);
  const key = valueId || valueName || 'default';
  const skuImg = colorAttr?.sku_img || colorAttr?.skuImg || null;
  let image = '';
  if (skuImg && typeof skuImg === 'object') {
    if (Array.isArray(skuImg.urls) && skuImg.urls.length) {
      image = textOf(skuImg.urls[0]);
    } else {
      image = textOf(skuImg.url, skuImg.uri);
    }
  } else {
    image = textOf(skuImg);
  }
  return { key, label: valueName, image, attr: colorAttr };
}

/** 尺码：优先 name 含 size/尺码；否则取非颜色的下一个销售属性 */
function pickSizeFromSku(sku, colorAttr) {
  const attrs = salesAttrsOf(sku);
  if (!attrs.length) return { label: '', valueId: '' };
  const sizeAttr = attrs.find((a) => /size|尺码|尺寸/i.test(String(a?.name || '')))
    || attrs.find((a) => a !== colorAttr && !/color|colour|颜色|色彩/i.test(String(a?.name || '')))
    || (attrs.length > 1 ? attrs.find((a) => a !== colorAttr) : null)
    || null;
  return {
    label: textOf(sizeAttr?.value_name, sizeAttr?.valueName),
    valueId: textOf(sizeAttr?.value_id, sizeAttr?.valueId),
  };
}

function sumSkuStock(sku) {
  let total = 0;
  const inv = sku?.inventory;
  if (Array.isArray(inv)) {
    inv.forEach((wh) => {
      total += Number(wh?.quantity) || 0;
    });
  } else if (inv && typeof inv === 'object') {
    total += Number(inv.quantity) || 0;
  }
  if (Array.isArray(sku?.stock_infos)) {
    sku.stock_infos.forEach((n) => {
      total += Number(n?.available_stock) || 0;
      total += Number(n?.quantity) || 0;
    });
  }
  if (sku?.available_stock != null) {
    total += Number(sku.available_stock) || 0;
  }
  return total;
}

function mapPayloadSku(productItem, sku, color) {
  const skuId = textOf(sku?.id, sku?.sku_id, sku?.skuId);
  const price = sku?.price && typeof sku.price === 'object' ? sku.price : null;
  const salePrice = textOf(price?.sale_price, price?.salePrice, price?.amount);
  const stock = sumSkuStock(sku);
  const size = pickSizeFromSku(sku, color?.attr);
  const sellerSku = textOf(sku?.seller_sku, sku?.sellerSku);
  const listedSites = Array.isArray(productItem?.listedSites) && productItem.listedSites.length
    ? productItem.listedSites
    : (Array.isArray(productItem?.shopSites) ? productItem.shopSites : []);
  return {
    id: skuId || `${productItem.productId || productItem.id}-${color.key}-${size.valueId || size.label || 'sku'}`,
    productId: productItem.productId || productItem.platformSpuId || productItem.id,
    platformSpuId: productItem.platformSpuId || productItem.productId || productItem.id,
    platformSkuId: skuId,
    // 商家SKU = seller_sku（卖家自填）；勿回落 platformSkuId
    supplierSku: sellerSku,
    sellerSku,
    merchantSku: sellerSku,
    supplierCode: sellerSku,
    // 尺码：供列表「尺码：」与弹窗 skuSizeLabel 读取
    sizeName: size.label,
    sizeLabel: size.label,
    skuSpecLabel: size.label,
    skuSize: size.label,
    specName: size.label,
    title: productItem.title,
    primaryImageUrl: color.image || productItem.primaryImageUrl || productItem.mainImageUrl,
    salePrice: salePrice || productItem.salePrice,
    currency: textOf(price?.currency) || productItem.currency,
    stockQty: stock,
    stockAvailableQty: stock,
    status: productItem.status,
    publishStatus: productItem.publishStatus,
    listedAt: productItem.listedAt,
    createdAt: productItem.createdAt || null,
    publishedAt: productItem.publishedAt || null,
    fromLocalPublishRecord: Boolean(productItem.fromLocalPublishRecord),
    lastSyncedAt: productItem.lastSyncedAt || productItem.syncedAt,
    syncedAt: productItem.syncedAt,
    listedSites,
  };
}

function buildColorSkcs(productItem) {
  const payload = productItem?.payload;
  const skus = Array.isArray(payload?.skus) ? payload.skus : [];
  if (!skus.length) {
    return [{
      key: `${productItem.platformSpuId || productItem.id}-default`,
      skcId: '',
      image: productItem.primaryImageUrl || productItem.mainImageUrl || null,
      specLabel: textOf(productItem.skcSpecLabel, productItem.colorName),
      supplierCode: productItem.supplierCode || productItem.supplierItemNo || '',
      skus: [productItem],
      skuCount: Number(productItem.skuCount) > 0 ? Number(productItem.skuCount) : 1,
    }];
  }

  const colorMap = new Map();
  skus.forEach((sku) => {
    const color = pickColorFromSku(sku);
    if (!colorMap.has(color.key)) {
      colorMap.set(color.key, {
        key: `${productItem.platformSpuId || productItem.id}-${color.key}`,
        skcId: '',
        image: color.image || productItem.primaryImageUrl || productItem.mainImageUrl || null,
        specLabel: color.label,
        supplierCode: '',
        skus: [],
      });
    }
    const node = colorMap.get(color.key);
    if (!node.image && color.image) node.image = color.image;
    if (!node.specLabel && color.label) node.specLabel = color.label;
    const mapped = mapPayloadSku(productItem, sku, color);
    node.skus.push(mapped);
    if (!node.supplierCode && mapped.supplierCode) {
      node.supplierCode = mapped.supplierCode;
    }
  });

  return Array.from(colorMap.values()).map((skc) => ({
    ...skc,
    skuCount: skc.skus.length,
    stockTotal: skc.skus.reduce((sum, s) => sum + (Number(s.stockAvailableQty) || 0), 0),
  }));
}

export function buildTiktokProductTree(items) {
  const list = Array.isArray(items) ? items : [];
  const productMap = new Map();

  for (const item of list) {
    const productKey = textOf(item.productId, item.platformSpuId) || `product-${item.id}`;
    if (!productMap.has(productKey)) {
      productMap.set(productKey, {
        key: productKey,
        spuId: textOf(item.productId, item.platformSpuId) || '—',
        title: item.title || '—',
        image: item.primaryImageUrl || item.mainImageUrl,
        supplierCode: item.supplierCode || item.supplierItemNo || '',
        skcs: [],
        _seed: item,
      });
    }
    const productNode = productMap.get(productKey);
    if (!productNode.supplierCode && item.supplierCode) {
      productNode.supplierCode = item.supplierCode;
    }
    if ((!productNode.title || productNode.title === '—') && item.title) {
      productNode.title = item.title;
    }
    if (!productNode.image && (item.primaryImageUrl || item.mainImageUrl)) {
      productNode.image = item.primaryImageUrl || item.mainImageUrl;
    }
    // 同一 product_id 只建一次颜色树（列表已按 product 去重）
    if (!productNode.skcs.length) {
      productNode.skcs = buildColorSkcs(item);
    }
  }

  return Array.from(productMap.values()).map(({ _seed, ...spu }) => spu);
}

export function countTiktokTreeRows(spuTree) {
  return spuTree.reduce(
    (sum, spu) => sum + Math.max(1, spu.skcs.length),
    0,
  );
}

/** 列表勾选 / 导出：按 product_id（不是 SKU id） */
export function collectTiktokTreeProductIds(spuTree) {
  return collectTiktokTreeSpuKeys(spuTree);
}

/** 选择/删除用的产品键（TikTok = product_id） */
export function spuSelectionKey(spu) {
  const id = String(spu?.spuId || '').trim();
  if (id && id !== '—') return id;
  return String(spu?.key || '').trim();
}

export function collectTiktokTreeSpuKeys(spuTree) {
  const keys = [];
  for (const spu of spuTree || []) {
    const key = spuSelectionKey(spu);
    if (key) keys.push(key);
  }
  return keys;
}

export function collectSpuProductIds(spu) {
  const ids = [];
  for (const skc of spu.skcs) {
    for (const sku of skc.skus) {
      if (sku?.id != null) ids.push(String(sku.id));
    }
  }
  return ids;
}

export function sumSkcSales7d(skus) {
  let total = 0;
  let hasValue = false;
  for (const sku of skus) {
    const n = Number(sku?.salesLast7Days);
    if (!Number.isNaN(n)) {
      total += n;
      hasValue = true;
    }
  }
  return hasValue ? total : null;
}

export function sumSkcStock(skus, field = 'stockAvailableQty') {
  let total = 0;
  let hasValue = false;
  for (const sku of skus || []) {
    const raw = field === 'stockAvailableQty'
      ? (sku?.stockAvailableQty ?? sku?.stockQty)
      : sku?.[field];
    const n = Number(raw);
    if (!Number.isNaN(n)) {
      total += n;
      hasValue = true;
    }
  }
  return hasValue ? total : null;
}

export function pickSkcRepresentative(skus) {
  if (!skus?.length) return null;
  return skus[0];
}
